/**
 * RWBR — DUEL resolution. Rulebook p.iii, p.ix, p.xiv, p.xvi + the DUEL table on Board A.
 *
 * A black die of "6" in the RACE CAM phase means dueling. The metallic total is
 * read on Board A's DUEL table to name the instigator's BOX, not his place: box
 * numbers run 1-6 within a group, so TOP box 5 is 6th place overall and MIDDLE
 * box 5 is the pair sitting 16th and 17th.
 *
 * A duel is not a challenge. There is no defence — p.iii calls the target
 * "defenseless … trapped in a track position where he can't respond" — and
 * "someone is ALWAYS going to have the potential for trouble": win and the
 * target is in trouble, lose and the instigator is.
 *
 * TROUBLE itself (escape roll, repair roll, the yellow that follows) is the
 * trouble module's job. This one names the car and says why.
 */

import type { Dice } from './dice.ts';
import type { Car, RaceState } from './types.ts';
import { groupOf } from './types.ts';
import type {
  ChipDelta,
  DiceRecord,
  Modifier,
  PendingProblem,
  PendingTrouble,
  PositionDelta,
  RuleEvent,
} from './ruleEvent.ts';
import { carAt, fieldFromState, membersOf, nameOf, positionOf } from './ruleEvent.ts';
import { oddEvenRoll, resolveChipContest, type ChipOutcome } from './challenge.ts';

// ---------------------------------------------------------------------------
// Board A — the DUEL table
// ---------------------------------------------------------------------------

export type DuelGroup = 'top' | 'middle';

/** Ledger labels for the two rolls a duel makes. */
export const DUEL_TABLE_ROLL = 'DUEL table metallic total';
export const DUEL_RED_ROLL = 'duel red die';

/** p.ix — "resolve the duel by rolling the red die". */
export function duelRedRoll(red: number): DiceRecord {
  return { kind: 'd6', dice: [red], label: DUEL_RED_ROLL, faces: { red } };
}

export interface DuelChartEntry {
  /** Printed exactly as it appears on Board A. */
  text: string;
  /** null on the 7, which prints "FUME/FEUD!! (Roll Again)" instead of a car. */
  group: DuelGroup | null;
  /** Board box number 1-6 within that group, NOT a finishing position. */
  boxNumber: number | null;
  /** Board A prints "car" for the TOP rows and "cars" for the MIDDLE rows — a
   *  MIDDLE box holds two drivers and both duel (p.ix). */
  plural: boolean;
  feudFume: boolean;
}

/**
 * The DUEL table, read as a SUM of the two metallic dice (2-12).
 * Source: Board A (bd_01.jpg) and the "Feud Fume" sheet, which agree word for word.
 *
 * Note the table never names box 1 of either group: the car at the front of a
 * group is never made to instigate, which is the "built-in advantage for drivers
 * who are higher up in both the TOP and MIDDLE groups" described on p.iii.
 */
export const DUEL_INSTIGATOR_CHART: Readonly<Record<number, DuelChartEntry>> = {
  2: { text: '#2 car in TOP group.', group: 'top', boxNumber: 2, plural: false, feudFume: false },
  3: { text: '#3 car in TOP group.', group: 'top', boxNumber: 3, plural: false, feudFume: false },
  4: { text: '#4 car in TOP group.', group: 'top', boxNumber: 4, plural: false, feudFume: false },
  5: { text: '#5 car in TOP group.', group: 'top', boxNumber: 5, plural: false, feudFume: false },
  6: { text: '#6 car in TOP group.', group: 'top', boxNumber: 6, plural: false, feudFume: false },
  7: { text: 'FUME/FEUD!! (Roll Again)', group: null, boxNumber: null, plural: false, feudFume: true },
  8: { text: '#6 cars in MIDDLE group.', group: 'middle', boxNumber: 6, plural: true, feudFume: false },
  9: { text: '#5 cars in MIDDLE group.', group: 'middle', boxNumber: 5, plural: true, feudFume: false },
  10: { text: '#4 cars in MIDDLE group.', group: 'middle', boxNumber: 4, plural: true, feudFume: false },
  11: { text: '#3 cars in MIDDLE group.', group: 'middle', boxNumber: 3, plural: true, feudFume: false },
  12: { text: '#2 cars in MIDDLE group.', group: 'middle', boxNumber: 2, plural: true, feudFume: false },
};

/** Board A, TOP group: box n is (n+1)th place. Box 1 = 2nd, box 6 = 7th. */
export function topBoxPositions(box: number): number[] {
  return [box + 1];
}

/**
 * Board B, MIDDLE group: box n holds two cars, the ODD half deeper in the field.
 * p.ix — "In DUELS involving the MIDDLE group, the driver in the 'odd' half of
 * the box goes first", so the odd half is returned first.
 */
export function middleBoxPositions(box: number): number[] {
  return [7 + 2 * box, 6 + 2 * box];
}

// ---------------------------------------------------------------------------
// Reading the table
// ---------------------------------------------------------------------------

export interface DuelCallOptions {
  /**
   * What a metallic 7 does.
   *
   * AMBIGUITY. Board A prints "FUME/FEUD!! (Roll Again)" and never says what to
   * roll again ON; the intake notes flag this as unresolved. Two readings:
   *   (a) [default, true] the 7 fires a FEUD/FUME *and* you roll the DUEL table
   *       again to find an instigator, so the turn still contains a duel;
   *   (b) [false] "roll again" means the FEUD/FUME RESULTS roll prescribed on
   *       p.x, and a 7 produces a feud/fume INSTEAD of a duel.
   * Reading (b) has support in p.iii ("Also included under the DUEL result
   * category are FEUD and FUME results") and in p.x's "nothing happens — simply
   * proceed to the next turn" when the feud list is empty. Reading (a) is the
   * house ruling and the default; flip this flag to take (b).
   */
  sevenAlsoRollsForDuel?: boolean;
  /** Runaway guard for a chain of 7s. */
  maxRerolls?: number;
}

export interface DuelCall extends RuleEvent {
  eventType: 'duel-call';
  momentTag: 'duel.called' | 'duel.feud-fume-only' | 'duel.no-car';
  /** Instigators in resolution order — for a MIDDLE box, the odd half first. */
  instigatorIds: string[];
  /** Board positions the table pointed at, in the same order. */
  instigatorPositions: number[];
  /** p.ix/p.x — a 7 or metallic doubles adds a FEUD/FUME to the turn. */
  feudFumeTriggered: boolean;
  chartTotal: number;
  /** Every total rolled on the DUEL table, in order. More than one means a 7 came up. */
  chartRolls: number[];
  entry: DuelChartEntry;
}

/**
 * Read the DUEL table and name the instigator(s).
 *
 * `metallic` is the RACE CAM roll the caller already made (black die 6).
 * p.ix — "On DUEL results where the metallic dice are 'doubles,' you'll add a
 * FEUD/FUME result to the turn." p.x adds the 7.
 */
export function callDuel(
  state: RaceState,
  metallic: { total: number; doubles: boolean; dice?: readonly number[] },
  dice: Dice,
  options: DuelCallOptions = {},
): DuelCall {
  const rollForDuelOnSeven = options.sevenAlsoRollsForDuel ?? true;
  const maxRerolls = options.maxRerolls ?? 6;
  const order = fieldFromState(state);

  const chartRolls: number[] = [metallic.total];
  const diceRecords: DiceRecord[] = [
    {
      kind: '2d6-sum',
      dice: [...(metallic.dice ?? [])],
      total: metallic.total,
      label: DUEL_TABLE_ROLL,
      flags: { doubles: metallic.doubles },
    },
  ];
  let entry = DUEL_INSTIGATOR_CHART[metallic.total];
  if (!entry) throw new RangeError(`metallic total ${metallic.total} is not on the DUEL table (2-12)`);

  const feudFumeTriggered = entry.feudFume || metallic.doubles;

  // A 7 is the FEUD/FUME row. Under the default reading we roll the table again
  // for an instigator; repeated 7s only re-roll, they do not stack extra feuds.
  let rerolls = 0;
  while (entry.feudFume && rollForDuelOnSeven && rerolls < maxRerolls) {
    const again = dice.sum2d6();
    chartRolls.push(again.total);
    diceRecords.push({
      kind: '2d6-sum',
      dice: again.dice,
      total: again.total,
      label: `${DUEL_TABLE_ROLL} (re-roll off the 7)`,
      flags: { doubles: again.doubles },
    });
    entry = DUEL_INSTIGATOR_CHART[again.total];
    rerolls++;
  }

  const boxPositions =
    entry.group === 'top'
      ? topBoxPositions(entry.boxNumber as number)
      : entry.group === 'middle'
        ? middleBoxPositions(entry.boxNumber as number)
        : [];

  const instigators: Car[] = [];
  for (const p of boxPositions) {
    const car = carAt(order, p);
    if (car) instigators.push(car);
  }

  const momentTag: DuelCall['momentTag'] =
    entry.feudFume ? 'duel.feud-fume-only' : instigators.length === 0 ? 'duel.no-car' : 'duel.called';

  const names = instigators.map(nameOf).join(' and ');
  const description = entry.feudFume
    ? `Metallic ${metallic.total} — tempers, not racing. FEUD/FUME, and no duel from the table.`
    : instigators.length === 0
      ? `The DUEL table points at ${entry.text.replace(/\.$/, '')}, but that box is empty — nothing doing.`
      : `The DUEL table points at ${entry.text.replace(/\.$/, '')}: ${names} ` +
        `${instigators.length > 1 ? 'each get' : 'gets'} the chance to pick a fight` +
        (feudFumeTriggered ? ', and tempers are up elsewhere too — FEUD/FUME this turn.' : '.');

  return {
    eventType: 'duel-call',
    momentTag,
    ruleCitation: 'RB/ix + Board A',
    actorIds: instigators.map((c) => c.card.driverId),
    positionDeltas: [],
    modifiers: metallic.doubles
      ? [{ name: 'metallic-doubles-adds-feud-fume', value: 0, note: 'doubles on a DUEL add a FEUD/FUME result (p.ix)' }]
      : feudFumeTriggered
        ? [{ name: 'metallic-seven-is-feud-fume', value: 0, note: 'a metallic 7 on a DUEL is FEUD/FUME (p.x, Board A)' }]
        : [],
    dice: diceRecords,
    description,
    instigatorIds: instigators.map((c) => c.card.driverId),
    instigatorPositions: instigators.map((c) => positionOf(order, c.card.driverId)),
    feudFumeTriggered,
    chartTotal: chartRolls[chartRolls.length - 1],
    chartRolls,
    entry,
  };
}

// ---------------------------------------------------------------------------
// Choosing a target
// ---------------------------------------------------------------------------

/**
 * p.ix — "That driver(s) will then have the chance to DUEL with any driver
 * WITHIN the same group." The LEADER is his own group and is therefore never a
 * legal target; the one exception, the final-turn "charge" for the lead (p.xvi),
 * is a separate optional rule and is not implemented here.
 */
export function duelTargets(state: RaceState, instigatorId: string): Car[] {
  const order = fieldFromState(state);
  const pos = positionOf(order, instigatorId);
  return membersOf(order, groupOf(pos)).filter((c) => c.card.driverId !== instigatorId);
}

/**
 * A solitaire convenience, not a rule: go for the front of your own group, as
 * Kenseth does in the p.ix example. Returns null when the instigator has the
 * group to himself.
 */
export function defaultDuelTarget(state: RaceState, instigatorId: string): Car | null {
  return duelTargets(state, instigatorId)[0] ?? null;
}

// ---------------------------------------------------------------------------
// Resolving the duel
// ---------------------------------------------------------------------------

export type DuelMoment =
  | 'duel.declined'
  | 'duel.win'
  | 'duel.loss'
  | 'duel.chip-win'
  | 'duel.void-triangle';

export interface DuelOptions {
  /**
   * p.xiv lets a chip "win a challenge or duel"; the solitaire shortcut spends
   * it automatically. Off for head-to-head play.
   */
  useChips?: boolean;
  /**
   * AMBIGUITY — the rulebook contradicts itself. p.xiv says a chip can "fend off
   * a challenge or duel", but p.iii says the duel target is "defenseless …
   * trapped in a track position where he can't respond". Default follows p.iii:
   * the target cannot buy his way out. Set true to take p.xiv literally.
   */
  targetMayDefend?: boolean;
}

export interface DuelResult extends RuleEvent {
  eventType: 'duel';
  momentTag: DuelMoment;
  instigatorId: string;
  targetId: string | null;
  declined: boolean;
  red: number | null;
  gasPedal: number;
  /** Red die inside the gas pedal — the duel is won on this alone. */
  gasOk: boolean;
  /** Won before any chip changed it. */
  rawWin: boolean;
  won: boolean;
  chip: ChipOutcome;
  chipDeltas: ChipDelta[];
  /** Whoever is in trouble. Always exactly one car unless the duel was declined or voided. */
  pendingTrouble: PendingTrouble | null;
  pendingProblem: PendingProblem | null;
}

/**
 * Resolve one duel.
 *
 * p.ix — "You'll resolve the duel by rolling the red die, and comparing it to
 * the instigating driver's 'gas pedal' rating. If the red die roll is equal to
 * or less than the gas pedal rating, then he wins the duel. The instigating
 * driver takes the higher position, gains a performance chip, and the targeted
 * driver gets a TROUBLE result. However, if the red die roll is greater than the
 * driver's gas pedal rating, then the duel FAILS and the instigating driver gets
 * the TROUBLE result."
 *
 * Pass `targetId: null` to decline — p.ix, "a driver is not obligated to
 * instigate a DUEL … He may decline the opportunity and maintain his position."
 */
export function resolveDuel(
  state: RaceState,
  decision: { instigatorId: string; targetId: string | null },
  dice: Dice,
  options: DuelOptions = {},
): DuelResult {
  const useChips = options.useChips ?? true;
  const targetMayDefend = options.targetMayDefend ?? false;
  const order = fieldFromState(state);

  const instigatorPos = positionOf(order, decision.instigatorId);
  const instigator = order[instigatorPos - 1];
  const gas = instigator.card.gasPedal;

  if (decision.targetId === null) {
    return {
      eventType: 'duel',
      momentTag: 'duel.declined',
      ruleCitation: 'RB/ix',
      actorIds: [decision.instigatorId],
      positionDeltas: [],
      modifiers: [],
      dice: [],
      description: `${nameOf(instigator)} thinks better of it and settles for P${instigatorPos}.`,
      instigatorId: decision.instigatorId,
      targetId: null,
      declined: true,
      red: null,
      gasPedal: gas,
      gasOk: false,
      rawWin: false,
      won: false,
      chip: 'none',
      chipDeltas: [],
      pendingTrouble: null,
      pendingProblem: null,
    };
  }

  const targetPos = positionOf(order, decision.targetId);
  const target = order[targetPos - 1];
  if (decision.targetId === decision.instigatorId) {
    throw new Error('a driver cannot duel himself');
  }
  if (groupOf(targetPos) !== groupOf(instigatorPos)) {
    throw new Error(
      `illegal duel target: ${nameOf(instigator)} is in the ${groupOf(instigatorPos)} group and ` +
        `${nameOf(target)} is in the ${groupOf(targetPos)} group. p.ix restricts a duel to "any ` +
        `driver WITHIN the same group".`,
    );
  }

  const red = dice.d6('red');
  const gasOk = red <= gas;

  // p.xvi — TRIANGLE SYMBOL applies to duels as well as challenges: a red 6 on a
  // triangle car is an immediate unspecified PROBLEM.
  // AMBIGUITY: a red 6 also loses the duel, which would put the same car in
  // TROUBLE. Reading: "immediately" means the PROBLEM pre-empts, and the car
  // does not eat both a problem and a wreck off one die.
  if (instigator.card.triangle && red === 6) {
    return {
      eventType: 'duel',
      momentTag: 'duel.void-triangle',
      ruleCitation: 'RB/xvi',
      actorIds: [decision.instigatorId],
      positionDeltas: [],
      modifiers: [
        { name: 'triangle-symbol', value: 0, note: 'red 6 on a car carrying the triangle warning symbol (p.xvi)' },
      ],
      dice: [duelRedRoll(red)],
      description:
        `${nameOf(instigator)} dives at ${nameOf(target)} — red 6, and the triangle car breaks instead. ` +
        `No duel; he has a problem.`,
      instigatorId: decision.instigatorId,
      targetId: decision.targetId,
      declined: false,
      red,
      gasPedal: gas,
      gasOk,
      rawWin: false,
      won: false,
      chip: 'none',
      chipDeltas: [],
      pendingTrouble: null,
      pendingProblem: {
        driverId: decision.instigatorId,
        source: 'triangle',
        specified: false,
        ruleCitation: 'RB/xvi',
        description: `${nameOf(instigator)} carries the triangle and rolled a red 6 in a duel — an immediate unspecified PROBLEM.`,
      },
    };
  }

  const contest = resolveChipContest({
    rawSuccess: gasOk,
    challengerId: decision.instigatorId,
    challengerChips: instigator.chips,
    defenderId: decision.targetId,
    defenderChips: target.chips,
    dice,
    enabled: useChips,
    defenderMayDefend: targetMayDefend,
    venue: 'duel',
  });

  const won = contest.success;
  const modifiers: Modifier[] = [...contest.modifiers];
  const chipDeltas: ChipDelta[] = [...contest.chipDeltas];
  const positionDeltas: PositionDelta[] = [];

  if (won) {
    // "The instigating driver takes the higher position." AMBIGUITY: the
    // rulebook does not spell out where the target goes. Read as a straight
    // swap, the same exchange a won challenge produces — so if the instigator
    // was already ahead of his target, nobody moves.
    const higher = Math.min(instigatorPos, targetPos);
    const lower = Math.max(instigatorPos, targetPos);
    if (higher !== lower && instigatorPos !== higher) {
      positionDeltas.push({ driverId: decision.instigatorId, from: instigatorPos, to: higher });
      positionDeltas.push({ driverId: decision.targetId, from: targetPos, to: lower });
    }

    // "…gains a performance chip." AMBIGUITY: awarded only when the red die won
    // the duel outright. A duel salvaged by spending a chip does not also refund
    // one, which would make the chip free and permanent.
    if (gasOk) {
      chipDeltas.push({ driverId: decision.instigatorId, delta: +1, reason: 'won a duel (p.ix)' });
      modifiers.push({ name: 'duel-win-performance-chip', value: 1, note: 'the winner of a duel gains a performance chip (p.ix)' });
    }
  }

  const loserId = won ? decision.targetId : decision.instigatorId;
  const loser = won ? target : instigator;
  const pendingTrouble: PendingTrouble = {
    scope: 'driver',
    driverId: loserId,
    source: won ? 'duel-target' : 'duel-instigator-failed',
    ruleCitation: 'RB/ix',
  };

  const momentTag: DuelMoment =
    contest.outcome === 'challenger-spent' ? 'duel.chip-win' : won ? 'duel.win' : 'duel.loss';

  let description: string;
  if (won && contest.outcome === 'challenger-spent') {
    description =
      `${nameOf(instigator)} misses the mark on the dice — red ${red} against ${gas} gas — but cashes a ` +
      `performance chip and clears ${nameOf(loser)}, who is loose and in trouble.`;
  } else if (won) {
    description =
      `${nameOf(instigator)} gets under ${nameOf(target)} — red ${red} inside ${gas} gas. He takes ` +
      `P${Math.min(instigatorPos, targetPos)} and a performance chip; ${nameOf(target)} is left with nowhere to go and in trouble.`;
  } else {
    description =
      `${nameOf(instigator)} sends it and gets it wrong — red ${red} against ${gas} gas. The duel fails and ` +
      `${nameOf(instigator)} is the one in trouble.`;
  }

  return {
    eventType: 'duel',
    momentTag,
    ruleCitation: 'RB/ix',
    actorIds: [decision.instigatorId, decision.targetId],
    positionDeltas,
    modifiers,
    dice: [duelRedRoll(red), ...oddEvenRoll(contest.oddEvenDie)],
    description,
    instigatorId: decision.instigatorId,
    targetId: decision.targetId,
    declined: false,
    red,
    gasPedal: gas,
    gasOk,
    rawWin: gasOk,
    won,
    chip: contest.outcome,
    chipDeltas,
    pendingTrouble,
    pendingProblem: null,
  };
}
